import { logger } from 'monitor';
import { type ForwardedRef, forwardRef, useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import {
  Autocomplete,
  Box,
  formControlClasses,
  outlinedInputClasses,
  Stack,
  TextField,
  type TextFieldProps as MuiTextFieldProps,
} from 'ui';

import { countries, type CountryType } from '../CountryAutocompleteField/countries';

export type PhoneNumberFieldProps = MuiTextFieldProps & {
  name: string;
  withFlags?: boolean;
  'data-testid'?: string;
};

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const DEFAULT_COUNTRY = countries.find((country) => country.abbr === 'GB')!;
const DEFAULT_NUMBER = '';

const extractCodeAndNumber = (value: string) => {
  const country = countries.find((country) => value.startsWith(`+${country.code}`));
  if (!country) {
    logger.error('unable to find selected country code', {
      value: value,
    });
    return null;
  }

  return {
    country,
    phoneNumber: value.replace(`+${country.code}`, ''),
  };
};

const Field = (
  { required, name, label, withFlags = false, size, 'data-testid': dataTestId, ...rest }: PhoneNumberFieldProps,
  ref: ForwardedRef<HTMLInputElement>
) => {
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    control,
    name,
    rules: {
      pattern: {
        message: 'Invalid phone number. Must include country code, e.g. +447806574241',
        value: /^\+[1-9]\d{1,14}$/,
      },
      required: required ? 'This field is required' : false,
    },
  });
  const { onChange } = field;

  const [country, setCountry] = useState<CountryType>(DEFAULT_COUNTRY);
  const [phoneNumber, setPhoneNumber] = useState<string>(DEFAULT_NUMBER);

  const updateField = (code: string, number: string) => {
    onChange(`+${code}${number}`);
  };

  useEffect(() => {
    if (field.value && !phoneNumber) {
      // load country and phone number from field value
      const current = extractCodeAndNumber(field.value);
      if (!current) {
        return;
      }
      setCountry(current.country);
      setPhoneNumber(current.phoneNumber);
    }
  }, [field.value, phoneNumber]);

  return (
    <Stack
      direction="row"
      sx={{
        [`& .${formControlClasses.root}:nth-of-type(1)`]: {
          flex: `1 0 180px`,
        },
      }}
      data-testid={dataTestId}
    >
      <Autocomplete<CountryType>
        options={countries}
        value={country}
        disablePortal={false}
        onBlurCapture={(event) => {
          const value = (event.target as HTMLInputElement).value;
          if (!value) {
            return;
          }
          const extractedCountry = extractCodeAndNumber(value.startsWith('+') ? value : `+${value}`);
          if (!extractedCountry) {
            return;
          }

          if (extractedCountry.country.code === country.code) {
            return;
          }
          setCountry(extractedCountry.country);
        }}
        onChange={(_event, newValue) => {
          if (!newValue) {
            logger.debug('no country selected');
            return;
          }
          setCountry(newValue);
          updateField(newValue.code, phoneNumber);
        }}
        label="Country code"
        fullWidth={false}
        size={size}
        startAdornment={
          withFlags && country ? (
            <Box ml={1.5}>
              <img
                src={`https://flagcdn.com/40x30/${country.abbr.toLowerCase()}.webp`}
                width="20"
                height="15"
                alt={country.name}
                loading="lazy"
              />
            </Box>
          ) : null
        }
        sx={{
          [`& .${outlinedInputClasses.root}`]: {
            borderBottomRightRadius: 0,
            borderTopRightRadius: 0,
          },
        }}
        data-testid="country-code-autocomplete"
        getOptionLabel={(option) => `+${option.code}`}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={option.abbr}
            sx={{
              maxWidth: '180px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textWrap: 'nowrap',
            }}
          >
            {`+${option.code} ${option.name}`}
          </Box>
        )}
      />
      <TextField
        size={size}
        value={phoneNumber}
        fullWidth
        onChange={(event) => {
          setPhoneNumber(event.target.value);
          updateField(country.code, event.target.value);
        }}
        label={label}
        sx={{
          [`& .${outlinedInputClasses.root}`]: {
            borderBottomLeftRadius: 0,
            borderTopLeftRadius: 0,
          },
        }}
        data-testid="phone-number-input"
        ref={ref}
        {...rest}
        error={Boolean(fieldState.error)}
        helperText={fieldState.error?.message}
      />
    </Stack>
  );
};
export const PhoneNumberField = forwardRef(Field);
